import React from "react";
import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import Text from "../../../../connectui/component/display/Text";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Container from "../../../../connectui/component/layout/Container";
import VirtualizedListComponent from "../../../../connectui/component/layout/list/VirtualizedListComponent";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import Tooltip from "../../../../connectui/component/display/Tooltip";
import RadioButton from "../../../../connectui/component/input/RadioButton";
import ButtonText from "../../../../connectui/component/input/button/ButtonText";
import OutlinedButtonBase from "../../../../connectui/component/input/button/base/OutlinedButtonBase";
import {ReactComponent as InfoIcon} from "../../../../connectui/icons/info.svg";
import {useTranslation} from "react-i18next";
import StatusIcon from "../../../../connectui/component/display/StatusIcon";

const useStyles = (theme) =>
  buildStyles({
    listContainer: {
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.lg,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    rowContent: {
      display: "flex",
      alignItems: "stretch",
      gap: theme.spacing(2),
      height: "100%",
    },
    valueContainer: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(2),
      flex: 1,
      minWidth: 0,
      height: "100%",
    },
    fieldLabel: {
      flex: "0 0 120px",
      maxWidth: "120px",
      whiteSpace: "normal",
      wordBreak: "break-word",
      color: theme.colors.textSecondary,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      height: "100%",
    },
    autoMergedRow: {
      backgroundColor: theme.colors.gray50,
      opacity: 0.8,
      padding: theme.spacing(3),
      display: "flex",
      alignItems: "center",
      minHeight: "70px",
      justifyContent: "center",
    },
    valueSection: {
      flex: "1 1 200px",
      minWidth: "200px",
      borderRadius: theme.radius.md,
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      width: "100%",
      minWidth: 0,
      height: "100%",
    },
  });

const MergeGuestConflictsPage = ({
  currentPage,
  pageData,
  selectedFields,
  onSelectField,
  onBack,
  onContinue,
}) => {
  const {t} = useTranslation();
  const {theme} = useTheme();
  const classes = useStyles(theme);

  return (
    <>
      <Container sx={{marginBottom: theme.spacing(4)}}>
        <Text variant="displayXS" weight="semibold">
          Resolve Conflicts
        </Text>
        <Text
          sx={{paddingTop: theme.spacing(2), paddingBottom: theme.spacing(2)}}
          variant="textSM"
          weight="medium"
          color="textSecondary"
        >
          {t("merge-guest.review-details")}
        </Text>
      </Container>

      <Container sx={{marginBottom: theme.spacing(4), flex: 1, minHeight: 0}}>
        <Container className={classes.listContainer}>
          <Text
            variant="textLG"
            weight="semibold"
            sx={{padding: theme.spacing(3)}}
          >
            {currentPage === 1
              ? t("merge-guest.select-guest-details")
              : t("merge-guest.select-booking-listing-details")}
          </Text>
          {pageData.length === 0 ? (
            <FlexContainer
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "300px",
                padding: theme.spacing(3),
                textAlign: "center",
              }}
            >
              <Text variant="textMD" color="textSecondary">
                {currentPage === 1
                  ? t("merge-guest.no-guest-to-merge")
                  : t("merge-guest.no-booking-listing-to-merge")}
              </Text>
            </FlexContainer>
          ) : (
            <>
              <Container
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "120px minmax(200px, 1fr) minmax(200px, 1fr)",
                  padding: theme.spacing(3),
                  borderBottom: `1px solid ${theme.colors.border}`,
                }}
              >
                <Text
                  variant="textSM"
                  weight="medium"
                  color="textSecondary"
                ></Text>
                <Text variant="textSM" weight="medium" color="textSecondary">
                  PMS (Primary)
                </Text>
                <Text variant="textSM" weight="medium" color="textSecondary">
                  Airbnb
                </Text>
              </Container>
              <Container
                sx={{
                  height: "300px",
                  overflowY: "auto",
                  height: Math.min(pageData.length * 70, 300),
                }}
              >
                <VirtualizedListComponent
                  totalRows={pageData.length}
                  rowHeight={70}
                  isRowLoaded={({index}) => index < pageData.length}
                  loadMoreRows={() => Promise.resolve()}
                >
                  {(index) => {
                    const rowData = pageData[index];
                    return (
                      <Container
                        sx={{
                          borderBottom: `1px solid ${theme.colors.border}`,
                          "&:last-child": {
                            borderBottom: "none",
                          },
                          height: "70px",
                        }}
                        className={
                          rowData.isAutoResolved ? classes.autoMergedRow : ""
                        }
                      >
                        {rowData.isAutoResolved ? (
                          <Tooltip
                            tooltipContent={
                              <Text>{rowData.autoResolvedValue}</Text>
                            }
                          >
                            <FlexContainer
                              sx={{
                                alignItems: "center",
                                gap: theme.spacing(1),
                                width: "100%",
                                cursor: "pointer",
                                paddingLeft: theme.spacing(2),
                              }}
                            >
                              <SvgIcon
                                component={InfoIcon}
                                sx={{
                                  width: "16px",
                                  height: "16px",
                                  color: theme.colors.gray400,
                                }}
                              />
                              <Text variant="textSM" color="textSecondary">
                                Auto-merged {rowData.label}
                              </Text>
                            </FlexContainer>
                          </Tooltip>
                        ) : (
                          <Container className={classes.rowContent}>
                            <Text
                              variant="textSM"
                              className={classes.fieldLabel}
                            >
                              {rowData.label}
                            </Text>
                            <FlexContainer
                              alignItems="center"
                              className={classes.valueSection}
                              sx={{
                                backgroundColor:
                                  selectedFields[rowData.field] ===
                                  rowData.primary.value
                                    ? theme.colors.blue50
                                    : "transparent",
                              }}
                              onClick={() =>
                                onSelectField(
                                  rowData.field,
                                  rowData.primary.value,
                                  rowData.primary.display,
                                )
                              }
                            >
                              <RadioButton
                                checked={
                                  selectedFields[rowData.field] ===
                                  rowData.primary.value
                                }
                                onChange={() =>
                                  onSelectField(
                                    rowData.field,
                                    rowData.primary.value,
                                    rowData.primary.display,
                                  )
                                }
                              />
                              <Container className={classes.valueContainer}>
                                <Text
                                  variant="textSM"
                                  sx={{
                                    wordBreak: "break-word",
                                    width: "100%",
                                  }}
                                  color={
                                    selectedFields[rowData.field] ===
                                    rowData.primary.value
                                      ? "primary"
                                      : "textPrimary"
                                  }
                                >
                                  {rowData.primary.display}
                                </Text>
                              </Container>
                            </FlexContainer>
                            <FlexContainer
                              alignItems="center"
                              className={classes.valueSection}
                              sx={{
                                backgroundColor:
                                  selectedFields[rowData.field] ===
                                  rowData.secondary.value
                                    ? theme.colors.blue50
                                    : "transparent",
                              }}
                              onClick={() =>
                                onSelectField(
                                  rowData.field,
                                  rowData.secondary.value,
                                  rowData.secondary.display,
                                )
                              }
                            >
                              <RadioButton
                                checked={
                                  selectedFields[rowData.field] ===
                                  rowData.secondary.value
                                }
                                onChange={() =>
                                  onSelectField(
                                    rowData.field,
                                    rowData.secondary.value,
                                    rowData.secondary.display,
                                  )
                                }
                              />
                              <Container className={classes.valueContainer}>
                                <Text
                                  variant="textSM"
                                  sx={{
                                    wordBreak: "break-word",
                                    width: "100%",
                                  }}
                                  color={
                                    selectedFields[rowData.field] ===
                                    rowData.secondary.value
                                      ? "primary"
                                      : "textPrimary"
                                  }
                                >
                                  {rowData.secondary.display}
                                </Text>
                              </Container>
                            </FlexContainer>
                          </Container>
                        )}
                      </Container>
                    );
                  }}
                </VirtualizedListComponent>
              </Container>
            </>
          )}
        </Container>
      </Container>

      <Container
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
        }}
      >
        <FlexContainer alignItems="center" gap={theme.spacing(1)}>
          <StatusIcon
            color="gray400"
            sx={{
              opacity: currentPage === 1 ? 1 : 0.5,
            }}
          />
          <StatusIcon
            color="gray400"
            sx={{
              opacity: currentPage === 2 ? 1 : 0.5,
            }}
          />
        </FlexContainer>
      </Container>

      <Container
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: theme.spacing(2),
        }}
      >
        <ButtonText
          label="Back"
          buttonBase={OutlinedButtonBase}
          onClick={onBack}
          sx={{
            color: theme.colors.textSecondary,
            borderColor: theme.colors.border,
          }}
        />
        <ButtonText
          label="Continue"
          onClick={onContinue}
          color={theme.colors.white}
        />
      </Container>
    </>
  );
};

export default MergeGuestConflictsPage;
